import styles from './FormProgressPanel.module.scss'
import { MilitaryHearingLossFormState } from '../hooks/useMilitaryHearingLossForm/useMilitaryHearingLossForm'
import { EMAIL_REGEX, NI_REGEX, TEL_REGEX, SERVICE_NUMBER_REGEX, POSTCODE_REGEX, INTERNATIONAL_POSTCODE_MAX, mobileMinLength } from '../hooks/useMilitaryHearingLossForm/militaryHearingLossFormUtils'
import { alpha, colors } from '../../../../ui'

interface Props {
    form: MilitaryHearingLossFormState
    emailData8Valid: boolean
    mobileData8Valid: boolean
    dobError: boolean
    serviceDateErrors: Record<string, boolean>
    /** Whether an audiogram file has been attached (held outside form state). */
    hasAudiogram?: boolean
    hasPoid?: boolean
    hasUtilityBill?: boolean
}

interface CheckItem {
    label: string
    done: boolean
    error?: boolean
    sectionId: string
    fieldId?: string
}

interface Section {
    title: string
    sectionId: string
    items: CheckItem[]
}

const scrollToAndPulse = (sectionId: string, fieldId?: string) => {
    document.getElementById(sectionId)?.scrollIntoView({ behavior: 'smooth', block: 'start' })
    if (!fieldId) return
    // Wait for scroll to settle before pulsing
    setTimeout(() => {
        const el = document.getElementById(fieldId)
        if (!el || typeof el.animate !== 'function') return
        el.animate(
            [
                { backgroundColor: alpha(colors.accent, 0), boxShadow: `0 0 0 0 ${alpha(colors.accent, 0)}` },
                { backgroundColor: alpha(colors.accent, 0.18), boxShadow: `0 0 0 4px ${alpha(colors.accent, 0.35)}`, offset: 0.3 },
                { backgroundColor: alpha(colors.accent, 0), boxShadow: `0 0 0 0 ${alpha(colors.accent, 0)}` },
            ],
            { duration: 1100, easing: 'ease-out' },
        )
    }, 450)
}

export const FormProgressPanel = ({ form, emailData8Valid, mobileData8Valid, dobError, serviceDateErrors, hasAudiogram = false, hasPoid = false, hasUtilityBill = false }: Props) => {
    const t = (v: string) => v.trim() !== ''

    const emailValid =
        t(form.email) &&
        EMAIL_REGEX.test(form.email.trim()) &&
        emailData8Valid

    const emailError =
        t(form.email) && !emailValid

    const mobileValid =
        t(form.telephone) &&
        TEL_REGEX.test(form.telephone.trim()) &&
        form.telephone.trim().length >= mobileMinLength(form.telephoneCountry) &&
        form.telephone.trim().length <= 20 &&
        mobileData8Valid

    const mobileError =
        t(form.telephone) && !mobileValid

    const niError =
        t(form.nationalInsurance) && !NI_REGEX.test(form.nationalInsurance.replace(/\s/g, ''))

    // UK postcodes are required and checked; elsewhere the postcode is optional free text
    const isUk = form.country === 'GB'
    const postcodeValid = isUk
        ? t(form.postcode) && POSTCODE_REGEX.test(form.postcode.trim())
        : form.postcode.trim().length <= INTERNATIONAL_POSTCODE_MAX
    const postcodeError = t(form.postcode) && !postcodeValid

    const serviceItems: CheckItem[] = form.services.flatMap((s, i) => {
        const n = form.services.length > 1 ? ` ${i + 1}` : ''
        const serviceNumberValid = t(s.serviceNumber) && SERVICE_NUMBER_REGEX.test(s.serviceNumber.trim())
        return [
            { label: `Service${n}: Number`, done: serviceNumberValid, error: t(s.serviceNumber) && !serviceNumberValid, sectionId: 'mhl-section-services', fieldId: `mhl-field-service-${i}-number` },
            { label: `Service${n}: Branch`, done: s.branch !== '', sectionId: 'mhl-section-services', fieldId: `mhl-field-service-${i}-branch` },
            { label: `Service${n}: Enlistment date`, done: t(s.enlistmentDate), error: serviceDateErrors[`${s.id}-enlistment`], sectionId: 'mhl-section-services', fieldId: `mhl-field-service-${i}-enlistment` },
            // Discharge date only applies when not still serving.
            ...(s.activeService
                ? []
                : [{ label: `Service${n}: Discharge date`, done: t(s.dischargeDate), error: serviceDateErrors[`${s.id}-discharge`], sectionId: 'mhl-section-services', fieldId: `mhl-field-service-${i}-discharge` }]),
        ]
    })

    const sections: Section[] = [
        {
            title: 'Personal Details',
            sectionId: 'mhl-section-personal',
            items: [
                { label: 'Title', done: t(form.title), sectionId: 'mhl-section-personal', fieldId: 'mhl-field-title' },
                { label: 'First Name', done: form.firstname.trim().length >= 2, sectionId: 'mhl-section-personal', fieldId: 'mhl-field-firstName' },
                { label: 'Surname', done: form.lastname.trim().length >= 2, sectionId: 'mhl-section-personal', fieldId: 'mhl-field-surname' },
                { label: 'Date of Birth', done: t(form.dob), error: dobError, sectionId: 'mhl-section-personal', fieldId: 'mhl-field-dob' },
                { label: 'National Insurance', done: t(form.nationalInsurance) && NI_REGEX.test(form.nationalInsurance.replace(/\s/g, '')), error: niError, sectionId: 'mhl-section-personal', fieldId: 'mhl-field-ni' },
                { label: 'Proof of ID', done: hasPoid, sectionId: 'mhl-section-personal', fieldId: 'mhl-field-poid' },
                { label: 'Utility bill', done: hasUtilityBill, sectionId: 'mhl-section-personal', fieldId: 'mhl-field-utility-bill' },
            ],
        },
        {
            title: 'Address',
            sectionId: 'mhl-section-address',
            items: [
                { label: 'Address Line 1', done: t(form.address1), sectionId: 'mhl-section-address', fieldId: 'mhl-field-address1' },
                { label: 'City', done: t(form.address3), sectionId: 'mhl-section-address', fieldId: 'mhl-field-address1' },
                ...(isUk ? [] : [{ label: 'Country', done: t(form.country), sectionId: 'mhl-section-address', fieldId: 'mhl-field-country' }]),
                { label: 'Postcode', done: postcodeValid, error: postcodeError, sectionId: 'mhl-section-address', fieldId: 'mhl-field-postcode' },
            ],
        },
        {
            title: 'Contact',
            sectionId: 'mhl-section-contact',
            items: [
                { label: 'Mobile Number', done: mobileValid, error: mobileError, sectionId: 'mhl-section-contact', fieldId: 'mhl-field-mobile' },
                { label: 'Email Address', done: emailValid, error: emailError, sectionId: 'mhl-section-contact', fieldId: 'mhl-field-email' },
            ],
        },
        {
            title: 'Service Records',
            sectionId: 'mhl-section-services',
            items: serviceItems,
        },
        {
            title: 'Medical Indicators',
            sectionId: 'mhl-section-medical',
            items: [
                { label: 'Audiology result', done: form.audiology !== '', sectionId: 'mhl-section-medical', fieldId: 'mhl-field-audiology' },
                { label: 'Audiogram upload', done: hasAudiogram, sectionId: 'mhl-section-medical', fieldId: 'mhl-field-audiogram' },
                { label: 'Tinnitus', done: form.hearingLossOrTinnitus !== null, sectionId: 'mhl-section-medical', fieldId: 'mhl-field-tinnitus' },
                { label: 'Hyperacusis', done: form.hyperacusis !== null, sectionId: 'mhl-section-medical', fieldId: 'mhl-field-hyperacusis' },
                { label: 'Alternative Noise Exposure', done: form.alternativeNoiseExposure !== '', sectionId: 'mhl-section-medical', fieldId: 'mhl-field-altNoise' },
                { label: 'Alternate Civilian Exposure', done: form.alternateCivilianExposure !== null, sectionId: 'mhl-section-medical', fieldId: 'mhl-field-altCivilian' },
            ],
        },
        {
            title: 'Other',
            sectionId: 'mhl-section-other',
            items: [
                { label: 'AFCS / War Pension Claim', done: form.successfulAfcsWarPensionClaim !== null, sectionId: 'mhl-section-other', fieldId: 'mhl-field-afcs' },
                ...(form.successfulAfcsWarPensionClaim === true ? [{ label: 'Award Amount', done: form.afcsAwardAmount !== null && form.afcsAwardAmount > 0, sectionId: 'mhl-section-other', fieldId: 'mhl-field-afcs' }] : []),
                { label: 'NFCI Claim', done: form.nfciClaim !== null, sectionId: 'mhl-section-other', fieldId: 'mhl-field-nfci' },
                { label: 'Special Forces', done: form.specialForces !== null, sectionId: 'mhl-section-other', fieldId: 'mhl-field-specialForces' },
            ],
        },
        {
            title: 'Confirmation',
            sectionId: 'mhl-section-confirmation',
            items: [
                { label: 'Matrix opt-in confirmed', done: form.matrixOptInConfirmed, sectionId: 'mhl-section-confirmation', fieldId: 'mhl-field-matrixOptIn' },
            ],
        },
    ]

    const totalItems = sections.reduce((acc, s) => acc + s.items.length, 0)
    const doneItems = sections.reduce((acc, s) => acc + s.items.filter((i) => i.done).length, 0)
    const allDone = totalItems > 0 && doneItems === totalItems

    return (
        <aside className={`${styles.panel}${allDone ? ` ${styles.panelComplete}` : ''}`}>
            <div className={`${styles.panelHeader}${allDone ? ` ${styles.panelHeaderComplete}` : ''}`}>
                <span className={styles.panelTitle}>Progress</span>
                {allDone ? (
                    <span className={styles.panelCheck} aria-label="All complete">
                        <svg width="11" height="9" viewBox="0 0 11 9" fill="none" aria-hidden="true">
                            <path d="M1 4.5L4 7.5L10 1.5" stroke={colors.accent} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                        </svg>
                    </span>
                ) : (
                    <span className={styles.panelCount}>{doneItems}/{totalItems}</span>
                )}
            </div>
            <div className={styles.progressBar}>
                <div className={styles.progressFill} style={{ width: `${(doneItems / totalItems) * 100}%` }} />
            </div>
            <div className={styles.sections}>
                {sections.map((section) => {
                    const sectionDone = section.items.length > 0 && section.items.every((i) => i.done)
                    return (
                    <div key={section.title} className={styles.sectionGroup}>
                        <button
                            type="button"
                            className={`${styles.sectionTitle}${sectionDone ? ` ${styles.sectionTitleDone}` : ''}`}
                            onClick={() => {
                                const firstIncomplete = section.items.find((i) => !i.done)
                                const target = firstIncomplete ?? section.items[0]
                                scrollToAndPulse(target.sectionId, target.fieldId)
                            }}
                        >
                            {section.title}
                        </button>
                        <ul className={styles.list}>
                            {section.items.map((item) => (
                                <li
                                    key={item.label}
                                    className={`${styles.item} ${item.done ? styles.done : item.error ? styles.itemError : ''}`}
                                >
                                    <span className={styles.dot} />
                                    <button
                                        type="button"
                                        className={styles.itemLabel}
                                        onClick={() => scrollToAndPulse(item.sectionId, item.fieldId)}
                                    >
                                        {item.label}
                                    </button>
                                </li>
                            ))}
                        </ul>
                    </div>
                    )
                })}
            </div>
        </aside>
    )
}
